import { useDispatch, useSelector } from "react-redux";

import { CheckboxWithLabel } from "../common/components/CheckboxWithLabel";
import {
  ADD_TO_COMPARISON_LIST_FAIL,
  ADD_TO_COMPARISON_LIST_SUCCESS,
  addToComparison,
  COMPARISON_LIST_FULL,
  REMOVE_FROM_COMPARISON_LIST_FAIL,
  REMOVE_FROM_COMPARISON_LIST_SUCCESS,
} from "./redux/actions";
import { selectProductComparisonList } from "./redux/productComparisonSelectors";
import { type RootState } from "../redux/store";
import { redirectToUrl } from "../utils/url/urlUtils";
import { userIsLoggedIn } from "../utils/roles";
import { trackAddToComparisonClick } from "./analytics";
import { selectProductData } from "../redux/products/productsSelectors";
import { getProductComparisonNavigationEntryUrl } from "./productComparisonHelper";
import { useTranslation } from "react-i18next";
import { isKITProduct, type ProductData } from "~/models/ProductData";
import { Snackbar, SnackbarCloseButton } from "~/common/m3/Snackbar/Snackbar";
import { Button } from "~/common/components/Button/Button";
import { useState } from "react";
import { Portal } from "@mui/material";

interface ProductComparisonCheckboxProps {
  gaLocation: string;
  productCode: string;
  className?: string;
}

export function isProductComparisonCheckboxVisible(product: ProductData) {
  return !isKITProduct(product);
}

export const ProductComparisonCheckbox = ({ productCode, className, gaLocation }: ProductComparisonCheckboxProps) => {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const productInComparisonList = useSelector(({ productComparison }: RootState) => productComparison.comparisonList);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [baseMessage, setBaseMessage] = useState("");
  const [error, setError] = useState(false);

  const loggedIn = userIsLoggedIn();

  const productData = useSelector(selectProductData(productCode));

  const handleCloseClick = () => {
    setIsSnackbarOpen(false);
  };

  const isProductAlreadyInComparison = productInComparisonList.some((code) => code === productCode);

  const handleAddToComparison = () => {
    if (loggedIn) {
      const disp = dispatch(addToComparison(productCode));
      disp.then((res: { type: string }) => {
        switch (res.type) {
          case ADD_TO_COMPARISON_LIST_SUCCESS: {
            setBaseMessage("comparisonSnack.added");
            setError(false);
            break;
          }
          case REMOVE_FROM_COMPARISON_LIST_SUCCESS: {
            setBaseMessage("comparisonSnack.removed");
            setError(false);
            break;
          }
          case ADD_TO_COMPARISON_LIST_FAIL: {
            setBaseMessage("comparisonSnack.failedToAdd");
            setError(true);
            break;
          }
          case REMOVE_FROM_COMPARISON_LIST_FAIL: {
            setBaseMessage("comparisonSnack.failedToRemove");
            setError(true);
            break;
          }
          case COMPARISON_LIST_FULL: {
            setBaseMessage("comparisonSnack.full");
            setError(true);
            break;
          }
          default: {
            setBaseMessage("");
            setError(true);
            break;
          }
        }
        setIsSnackbarOpen(true);
      });
      trackAddToComparisonClick(productData.designation, !isProductAlreadyInComparison, gaLocation);
    } else {
      redirectToUrl(getProductComparisonNavigationEntryUrl(productCode));
    }
  };

  return (
    <>
      <CheckboxWithLabel
        tracking-aux-ppr-comparison="true"
        className={className}
        checked={isProductAlreadyInComparison}
        onCheck={handleAddToComparison}
      >
        {t("add.to.comparison.list")}
      </CheckboxWithLabel>
      <Portal container={document.body}>
        <ProductComparisonSnackbar
          open={isSnackbarOpen}
          message={baseMessage}
          apiError={error}
          onClose={handleCloseClick}
        />
      </Portal>
    </>
  );
};

type Props = {
  open?: boolean;
  message?: string;
  apiError?: boolean;
  onClose?: () => void;
};
export const ProductComparisonSnackbar = (props: Props) => {
  const { t } = useTranslation();
  const listItems = useSelector(selectProductComparisonList).length;
  const { maxSize } = window.SCHA.productComparisonList;
  const PRODUCT_COMPARISON_URL = getProductComparisonNavigationEntryUrl();

  return (
    <Snackbar
      {...props}
      message={t(`${props.message}`, { listItems, maxSize })}
      action={
        <>
          {!props.apiError && (
            <Button
              href={PRODUCT_COMPARISON_URL}
              id="snack_comparison_list"
              color={"inversePrimary"}
              variant={"text"}
              size={"medium"}
            >
              {t("comparisonSnack.view")}
            </Button>
          )}
          <SnackbarCloseButton onClick={props.onClose} />
        </>
      }
    />
  );
};
